/**
 * CommunityCommsThread — the PEOC↔COMMUNITY two-way COORDINATION log (#420a, role-view
 * council §6, the §E sim G2-closer). Bound to a community RECORD (not a tasked agency,
 * not an incident), reachable from the community / flood detail incident-free.
 *
 * WHY a thin component, not the shipped MissionThreadPanel / MessageThread: those key
 * "mine", labels, and read receipts on the TWO-PARTY `author_role` enum
 * ('requestor'|'operator') + two readBy*At columns. This is an AMBIENT one-author /
 * many-reader log — "mine" rides each message from the server (`m.mine`), the author label is
 * server-DERIVED data (`m.author_label`, the seat today), and the per-message receipt (#453) is
 * a MANY-reader model (`m.read_by`) rather than the two-party readBy*At. Reusing the two-party
 * kit would mean forking its shared model; this is the minimal honest generalization instead. It
 * does reuse the kit's VISUAL idiom (the same row/composer Tailwind).
 *
 * #453 — PER-MESSAGE RECEIPT. An EXPLICIT "Acknowledge" affordance on NON-mine messages (never
 * auto-on-render); a "Acknowledged · HH:MM" chip once the viewer acknowledges; and, on the
 * author's OWN messages, "Acknowledged by <seat> · HH:MM" per acknowledging seat. A receipt
 * exists only for a real in-app acknowledge (the honesty note is rendered on the surface).
 */
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import type { CommunityMessage } from '@tsr/shared-types';
import { fetchCommunityMessages, postCommunityMessage, acknowledgeCommunityMessage } from './api';
import {
  ACKNOWLEDGE_LABEL,
  ACKNOWLEDGING_LABEL,
  acknowledgedAtLabel,
  acknowledgedByLabel,
  COMMUNITY_RECEIPT_HONESTY_NOTE,
  OPERATOR_COORDINATION_DISCLAIMER,
  coordinatingOnLabel,
  hazardName,
  hazardProvenanceLabel,
  coordinatingAboutHazardLabel,
} from './communityCommsVocabulary';

/** Migration 0154 — the hazard this thread is mounted FROM (the OC-fire panel). Present ⇒ every
 *  message posted here is STAMPED with (sourceType, sourceId) so the community gets a HAZARD-LINKED
 *  record. `ref` is the human fire number (for the header); `contextLabel` is optional FACT-ONLY
 *  live enrichment (area / distance / access). Absent ⇒ the thread posts unbound (community/flood
 *  mounts, byte-identical to before). */
export interface CommunityCommsHazard {
  sourceType: string;
  sourceId: string;
  ref?: string | null;
  contextLabel?: string;
}

/** Compact, locale-free `MM-DD HH:MM UTC` from an ISO stamp (deterministic for tests). */
function fmtTime(iso: string): string {
  return iso.length >= 16 ? `${iso.slice(5, 10)} ${iso.slice(11, 16)} UTC` : iso;
}

/** The bare `HH:MM` for the receipt chip (the shipped "Acknowledged · HH:MM" family). */
function hhmmUtc(iso: string): string {
  return iso.length >= 16 ? iso.slice(11, 16) : iso;
}

export function CommunityCommsThread({
  communityId,
  /** When mounted from a context other than the community itself (e.g. the flood
   *  detail's primary in-reach community), name the bound community so the operator
   *  knows who the thread coordinates with. */
  boundLabel,
  /** Migration 0154 — when mounted FROM a hazard (the OC-fire panel), bind every post to it and
   *  show which fire this coordination cycle is about. Omit for an unbound thread. */
  hazard,
}: {
  communityId: string;
  boundLabel?: string;
  hazard?: CommunityCommsHazard;
}): JSX.Element {
  const [messages, setMessages] = useState<CommunityMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [acking, setAcking] = useState<Set<string>>(new Set());

  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    void fetchCommunityMessages(communityId, ac.signal)
      .then((msgs) => {
        if (alive) setMessages(msgs);
      })
      .catch(() => {});
    return () => {
      alive = false;
      ac.abort();
    };
  }, [communityId]);

  async function handleSend(e: FormEvent): Promise<void> {
    e.preventDefault();
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      // Migration 0154 — stamp the hazard binding when this thread is mounted from a fire, so the
      // message carries a hazard-linked record. The UNBOUND path (community/flood mounts) calls with
      // exactly two args — byte-identical to before (Pin #4 regression lock).
      const created = hazard
        ? await postCommunityMessage(communityId, content, {
            source_type: hazard.sourceType,
            source_id: hazard.sourceId,
          })
        : await postCommunityMessage(communityId, content);
      setMessages((prev) => [...prev, created]);
      setDraft('');
    } catch {
      // On failure re-pull the authoritative thread (keeps the draft for a retry).
      try {
        setMessages(await fetchCommunityMessages(communityId));
      } catch {
        /* keep current */
      }
    } finally {
      setSending(false);
    }
  }

  async function handleAcknowledge(messageId: string): Promise<void> {
    if (acking.has(messageId)) return;
    setAcking((prev) => new Set(prev).add(messageId));
    try {
      const updated = await acknowledgeCommunityMessage(communityId, messageId);
      setMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    } catch {
      // Best-effort: re-pull the authoritative thread so the receipt state stays honest.
      try {
        setMessages(await fetchCommunityMessages(communityId));
      } catch {
        /* keep current */
      }
    } finally {
      setAcking((prev) => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  }

  const canSend = !sending && draft.trim().length > 0;

  return (
    <section
      className="border-t border-slate-800 pt-3"
      data-testid="community-comms-thread"
    >
      <h3 className="text-xs uppercase tracking-wider text-slate-400">Operator coordination</h3>
      {/* Gate correction 4 — make it unmistakable this is an INTERNAL coordination log,
          not a resident-facing bulletin (the resident push is #420b). */}
      <p className="mt-0.5 text-[10px] text-slate-500" data-testid="community-comms-disclaimer">
        {OPERATOR_COORDINATION_DISCLAIMER}
        {boundLabel ? ` ${coordinatingOnLabel(boundLabel)}` : ''}
      </p>
      {/* Migration 0154 — when mounted from a fire, name WHICH fire this coordination is about, so
          the operator (and, in the record, the community) can tell the fires apart. FACT-ONLY. */}
      {hazard && (
        <p
          className="mt-0.5 text-[10px] font-medium text-amber-300/90"
          data-testid="community-comms-hazard-binding"
        >
          {coordinatingAboutHazardLabel(
            hazardName(hazard.sourceType, hazard.ref ?? null),
            hazard.contextLabel,
          )}
        </p>
      )}

      <ul
        className="mt-2 flex max-h-72 flex-col gap-1.5 overflow-y-auto"
        data-testid="community-comms-list"
      >
        {messages.length === 0 ? (
          <li className="text-[11px] text-slate-500">No coordination messages yet.</li>
        ) : (
          messages.map((m) => {
            const readBy = m.read_by ?? [];
            return (
              <li
                key={m.id}
                className={`flex flex-col ${m.mine ? 'items-end' : 'items-start'}`}
                data-testid="community-comms-item"
                data-mine={m.mine ? 'true' : 'false'}
                data-author={m.author_operator_id}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-2.5 py-1.5 text-[12px] ${
                    m.mine
                      ? 'bg-cyan-600/30 border border-cyan-500/40 text-slate-100'
                      : 'bg-slate-800/70 border border-slate-700 text-slate-200'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{m.content}</p>
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 px-1 text-[9px] text-slate-500">
                  <span data-testid="community-comms-author">{m.author_label}</span>
                  <span className="font-mono">{fmtTime(m.created_at)}</span>
                </div>
                {/* Migration 0154 — a message posted from a fire panel names WHICH fire, so the
                    record stands up in an after-action. Absent on unbound messages (byte-identical
                    to before). Derived from the message's OWN hazard binding (server data). */}
                {m.hazard && (
                  <span
                    className="px-1 text-[9px] font-medium text-amber-300/80"
                    data-testid="community-comms-hazard"
                    data-hazard-source-type={m.hazard.source_type}
                    data-hazard-source-id={m.hazard.source_id}
                  >
                    {hazardProvenanceLabel(m.hazard.source_type, m.hazard.source_ref)}
                  </span>
                )}

                {/* #453 per-message receipt. On MY message: who acknowledged it. On another
                    seat's message: my explicit Acknowledge affordance → my receipt chip. */}
                {m.mine ? (
                  readBy.length > 0 ? (
                    <div
                      className="mt-0.5 flex flex-col gap-0.5 px-1"
                      data-testid="community-comms-receipts"
                    >
                      {readBy.map((rcpt, i) => (
                        <span
                          key={`${m.id}-rcpt-${i}`}
                          data-testid="community-comms-receipt"
                          className="text-[9px] text-emerald-400/80"
                        >
                          {acknowledgedByLabel(rcpt.label, hhmmUtc(rcpt.at))}
                        </span>
                      ))}
                    </div>
                  ) : null
                ) : m.acknowledged_by_me ? (
                  <span
                    className="mt-0.5 px-1 text-[9px] text-emerald-400/80"
                    data-testid="community-comms-acked"
                  >
                    {acknowledgedAtLabel(hhmmUtc(m.acknowledged_by_me_at ?? m.created_at))}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => void handleAcknowledge(m.id)}
                    disabled={acking.has(m.id)}
                    data-testid="community-comms-ack-btn"
                    className="mt-0.5 rounded border border-slate-700 px-1.5 py-0.5 text-[9px] text-slate-300 hover:border-slate-500 hover:text-slate-100 disabled:opacity-50"
                  >
                    {acking.has(m.id) ? ACKNOWLEDGING_LABEL : ACKNOWLEDGE_LABEL}
                  </button>
                )}
              </li>
            );
          })
        )}
      </ul>

      <form onSubmit={(e) => void handleSend(e)} className="mt-2 flex items-end gap-1.5" data-testid="community-comms-composer">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="Coordinate with this community…"
          data-testid="community-comms-input"
          className="min-w-0 flex-1 resize-none rounded border border-slate-700 bg-slate-950/60 px-2 py-1 text-[12px] text-slate-100 placeholder:text-slate-500 focus:border-slate-500 focus:outline-none"
        />
        <button
          type="submit"
          disabled={!canSend}
          data-testid="community-comms-send"
          className="shrink-0 rounded bg-cyan-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Send'}
        </button>
      </form>

      {/* #453 honesty note — a receipt exists only for a real in-app acknowledge. */}
      <p className="mt-1.5 text-[9px] text-slate-600" data-testid="community-comms-receipt-note">
        {COMMUNITY_RECEIPT_HONESTY_NOTE}
      </p>
    </section>
  );
}
